/**
 * MSAL-Instanz — Pendant zu `AddMsalAuthentication(...)` aus `Program.cs` des Altclients
 * inklusive der dort registrierten `AuthorizationMessageHandler`.
 */

import type { AccountInfo } from '@azure/msal-browser';
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';

import { setTokenProvider } from '@/api/requestContext';
import { msalConfig } from '@/config/authConfig';

import { getDemoAccount, isDemoMode } from './demoAccount';

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Konto der laufenden Sitzung — Pendant zu `AuthenticationState.User`.
 *
 * Nach einer Weiterleitung kennt MSAL das Konto, markiert es aber nicht von sich aus als
 * aktiv. Das erste bekannte Konto wird deshalb nachgezogen, damit `getActiveAccount`
 * anschließend überall dasselbe Konto liefert.
 */
export function getAuthenticatedAccount(): AccountInfo | null {
  // Im Demobetrieb gilt der vorgegebene Benutzer als angemeldet; MSAL wird nicht befragt.
  if (isDemoMode) {
    return getDemoAccount();
  }

  const activeAccount = msalInstance.getActiveAccount();
  if (activeAccount !== null) {
    return activeAccount;
  }

  const [firstAccount] = msalInstance.getAllAccounts();
  if (firstAccount === undefined) {
    return null;
  }

  msalInstance.setActiveAccount(firstAccount);
  return firstAccount;
}

/**
 * Zugriffstoken für die API-Schicht — Pendant zum `AuthorizationMessageHandler`, der im
 * Altclient jeder Anfrage ein Token für die angeforderten Scopes beilegt.
 *
 * Die Registrierung geschieht beim Laden des Moduls und damit unabhängig davon, ob
 * `initializeMsal` oder `msalInstance.initialize()` den Start übernimmt.
 */
setTokenProvider(async (scopes) => {
  // Der Demobetrieb der API prüft kein Token; der Wert dient nur dazu, dass der
  // Authorization-Kopf wie im Echtbetrieb gesetzt wird.
  if (isDemoMode) {
    return 'demo';
  }

  const account = getAuthenticatedAccount();
  if (account === null) {
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes, account });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes, account });
    }
    return null;
  }
});

/** Von `main.tsx` vor dem ersten Rendern aufgerufen. */
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();

  // Ohne echte Anmeldung gibt es keine offene Weiterleitung, die abzuschließen wäre.
  if (isDemoMode) {
    return;
  }

  await msalInstance.handleRedirectPromise();
  getAuthenticatedAccount();
}
